import { Linking, Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";

// TODO(links): replace with the real App Store / Play Store listings.
const IOS_APP_URL = "https://haniverse.app/ios";
const ANDROID_APP_URL = "https://haniverse.app/android";

/**
 * Why the database layer refused to start. `no-opfs` is a browser limitation the
 * user can route around; `no-cross-origin-isolation` is a SERVER misconfiguration
 * (missing COOP/COEP headers) that no amount of switching browsers will fix — so
 * they get different copy. Telling a self-hoster "this browser isn't supported"
 * when the real fix is two response headers sends them down a dead end.
 */
export type UnsupportedBrowserReason = "no-cross-origin-isolation" | "no-opfs";

export function UnsupportedBrowserScreen({
  reason = "no-opfs",
}: {
  reason?: UnsupportedBrowserReason;
}) {
  if (reason === "no-cross-origin-isolation") {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-white p-8 dark:bg-black">
        <View className="max-w-md gap-3">
          <Text className="text-xl font-bold">Haniverse can’t start on this page</Text>
          <Text className="text-sm opacity-80">
            Haniverse’s database engine needs this page to be cross-origin isolated, which requires
            the server to send the{" "}
            <Text className="font-mono text-xs">Cross-Origin-Opener-Policy</Text> and{" "}
            <Text className="font-mono text-xs">Cross-Origin-Embedder-Policy</Text> headers. If
            you’re running Haniverse yourself, add those headers; otherwise get the app:
          </Text>
          <View className="mt-2 flex-row gap-3">
            <StoreButton label="iOS app" url={IOS_APP_URL} />
            <StoreButton label="Android app" url={ANDROID_APP_URL} />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View className="flex-1 items-center justify-center gap-4 bg-white p-8 dark:bg-black">
      <View className="max-w-md gap-3">
        <Text className="text-xl font-bold">This browser isn’t supported</Text>
        <Text className="text-sm opacity-80">
          Haniverse needs local storage to work, which this browser doesn’t provide — you may be in
          a private or incognito window, or an in-app browser. Open Haniverse in a normal Chrome,
          Safari, or Edge window, or get the app:
        </Text>
        <View className="mt-2 flex-row gap-3">
          <StoreButton label="iOS app" url={IOS_APP_URL} />
          <StoreButton label="Android app" url={ANDROID_APP_URL} />
        </View>
      </View>
    </View>
  );
}

function StoreButton({ label, url }: { label: string; url: string }) {
  return (
    <Pressable
      accessibilityRole="button"
      className="rounded-xs bg-black px-4 py-2 dark:bg-white"
      onPress={() => {
        void Linking.openURL(url);
      }}
    >
      <Text className="text-sm font-semibold text-white dark:text-black">{label}</Text>
    </Pressable>
  );
}
